import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import BridgeClubLayout from '../../components/BridgeClub/BridgeClubLayout';
import { bridgeClubAPI } from '../../services/bridgeClubAPI';
import { useLocalAccess } from '../../hooks/useLocalAccess';
import './ScanAndGoCheckout.css';

interface SavedPaymentMethod {
  id: number;
  type: 'card' | 'ach';
  card_type?: string;
  last_four: string;
  expiry_month?: number;
  expiry_year?: number;
  bank_name?: string;
  is_default: boolean;
  nickname?: string;
}

interface SessionItem {
  id: number;
  product: {
    id: number;
    name: string;
    price: string;
  };
  quantity: number;
  weight_lbs?: string;
  is_weighted: boolean;
  line_total: string;
}

interface ScanSession {
  session_id: string;
  status: string;
  items: SessionItem[];
  subtotal: string;
  tax: string;
  total: string;
  item_count: number;
}

const ScanAndGoCheckout: React.FC = () => {
  const navigate = useNavigate();
  const { hasLocalAccess } = useLocalAccess();
  const [session, setSession] = useState<ScanSession | null>(null);
  const [paymentMethods, setPaymentMethods] = useState<SavedPaymentMethod[]>([]);
  const [selectedPaymentId, setSelectedPaymentId] = useState<number | null>(null);
  const [saveCard, setSaveCard] = useState(false);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');
  const [pendingSessionId, setPendingSessionId] = useState<string | null>(null);

  // Listen for Helcim Pay modal completion
  const handleHelcimMessage = useCallback((event: MessageEvent) => {
    if (!event.origin.includes('helcim.com')) return;
    const data = event.data;
    if (!data || typeof data !== 'object') return;

    if (data.eventName === 'helcim-pay-success' || data.eventStatus === 'APPROVED') {
      if (pendingSessionId) {
        navigate(`/bridge-club/scan-and-go/receipt/${pendingSessionId}`);
      }
    } else if (data.eventName === 'helcim-pay-error' || data.eventStatus === 'DECLINED' || data.eventStatus === 'ERROR') {
      setProcessing(false);
      setError(data.error || data.message || 'Payment was declined. Please try again.');
    } else if (data.eventName === 'helcim-pay-close') {
      setProcessing(false);
      if (pendingSessionId) {
        navigate(`/bridge-club/scan-and-go/receipt/${pendingSessionId}`);
      }
    }
  }, [pendingSessionId, navigate]);

  useEffect(() => {
    window.addEventListener('message', handleHelcimMessage);
    return () => window.removeEventListener('message', handleHelcimMessage);
  }, [handleHelcimMessage]);

  useEffect(() => {
    if (!hasLocalAccess) {
      navigate('/bridge-club');
      return;
    }
    loadData();
  }, [hasLocalAccess, navigate]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [sessionData, methodsData] = await Promise.all([
        bridgeClubAPI.getScanAndGoSession(),
        bridgeClubAPI.getSavedPaymentMethods(),
      ]);

      if (!sessionData || sessionData.items.length === 0) {
        navigate('/bridge-club/scan-and-go');
        return;
      }

      setSession(sessionData);
      setPaymentMethods(methodsData || []);

      // Select default payment method
      const defaultMethod = methodsData?.find((m: SavedPaymentMethod) => m.is_default);
      if (defaultMethod) {
        setSelectedPaymentId(defaultMethod.id);
      }
    } catch (err) {
      console.error('Failed to load checkout data:', err);
      setError('Failed to load checkout data');
    } finally {
      setLoading(false);
    }
  };

  const handleCheckout = async () => {
    if (!session) return;

    setProcessing(true);
    setError('');

    try {
      const result = await bridgeClubAPI.scanAndGoCheckout(
        selectedPaymentId || undefined,
        saveCard
      );

      if (result.success) {
        // Navigate to receipt/confirmation page
        navigate(`/bridge-club/scan-and-go/receipt/${result.session_id}`);
      } else if (result.checkout_token) {
        // Open embedded Helcim Pay modal for new card
        if (window.appendHelcimPayIframe) {
          setPendingSessionId(result.session_id);
          window.appendHelcimPayIframe(result.checkout_token);
        } else if (result.payment_url) {
          // Fallback to redirect
          window.location.href = result.payment_url;
        }
      } else {
        setError(result.error || 'Payment failed');
      }
    } catch (err: any) {
      console.error('Checkout failed:', err);
      setError(err.message || 'Failed to process payment');
    } finally {
      setProcessing(false);
    }
  };

  const getCardIcon = (cardType?: string) => {
    switch (cardType?.toLowerCase()) {
      case 'visa': return '💳';
      case 'mastercard': return '💳';
      case 'amex': return '💳';
      case 'discover': return '💳';
      default: return '💳';
    }
  };

  if (loading) {
    return (
      <BridgeClubLayout>
        <div className="checkout-loading">
          <div className="spinner"></div>
          <p>Loading...</p>
        </div>
      </BridgeClubLayout>
    );
  }

  if (!session) {
    return (
      <BridgeClubLayout>
        <div className="checkout-error">
          <p>No items to checkout</p>
          <button onClick={() => navigate('/bridge-club/scan-and-go')}>
            Continue Shopping
          </button>
        </div>
      </BridgeClubLayout>
    );
  }

  return (
    <BridgeClubLayout>
      <div className="scan-checkout">
        {/* Order Summary */}
        <div className="order-summary">
          <h3>Order Summary</h3>
          <div className="summary-items">
            {session.items.map((item) => (
              <div key={item.id} className="summary-item">
                <span className="item-name">
                  {item.product.name}
                  {item.is_weighted ? ` (${item.weight_lbs} lbs)` : ` x${item.quantity}`}
                </span>
                <span className="item-price">${parseFloat(item.line_total).toFixed(2)}</span>
              </div>
            ))}
          </div>
          <div className="summary-totals">
            <div className="total-row">
              <span>Subtotal</span>
              <span>${parseFloat(session.subtotal).toFixed(2)}</span>
            </div>
            <div className="total-row">
              <span>Tax (8.0%)</span>
              <span>${parseFloat(session.tax).toFixed(2)}</span>
            </div>
            <div className="total-row grand-total">
              <span>Total</span>
              <span>${parseFloat(session.total).toFixed(2)}</span>
            </div>
          </div>
        </div>

        {/* Payment Methods */}
        <div className="payment-section">
          <h3>Payment Method</h3>

          {paymentMethods.length > 0 ? (
            <div className="saved-methods">
              {paymentMethods.map((method) => (
                <label
                  key={method.id}
                  className={`payment-method ${selectedPaymentId === method.id ? 'selected' : ''}`}
                >
                  <input
                    type="radio"
                    name="paymentMethod"
                    checked={selectedPaymentId === method.id}
                    onChange={() => setSelectedPaymentId(method.id)}
                  />
                  <div className="method-info">
                    <span className="method-icon">{getCardIcon(method.card_type)}</span>
                    <div className="method-details">
                      {method.type === 'card' ? (
                        <>
                          <span className="method-name">
                            {method.card_type} ****{method.last_four}
                          </span>
                          <span className="method-expiry">
                            Expires {method.expiry_month}/{method.expiry_year}
                          </span>
                        </>
                      ) : (
                        <>
                          <span className="method-name">
                            {method.bank_name} ****{method.last_four}
                          </span>
                          <span className="method-type">Bank Account</span>
                        </>
                      )}
                    </div>
                    {method.is_default && <span className="default-badge">Default</span>}
                  </div>
                </label>
              ))}

              <label
                className={`payment-method ${selectedPaymentId === null ? 'selected' : ''}`}
              >
                <input
                  type="radio"
                  name="paymentMethod"
                  checked={selectedPaymentId === null}
                  onChange={() => setSelectedPaymentId(null)}
                />
                <div className="method-info">
                  <span className="method-icon">+</span>
                  <span className="method-name">Use a new card</span>
                </div>
              </label>
            </div>
          ) : (
            <div className="no-saved-methods">
              <p>No saved payment methods</p>
              <p className="hint">You'll enter your card details on the next screen</p>
            </div>
          )}

          {selectedPaymentId === null && (
            <label className="save-card-option">
              <input
                type="checkbox"
                checked={saveCard}
                onChange={(e) => setSaveCard(e.target.checked)}
              />
              <span>Save this card for future purchases</span>
            </label>
          )}
        </div>

        {error && <div className="checkout-error-message">{error}</div>}

        {/* Pay Button */}
        <div className="checkout-actions">
          <button
            className="pay-btn"
            onClick={handleCheckout}
            disabled={processing}
          >
            {processing ? 'Processing...' : `Pay $${parseFloat(session.total).toFixed(2)}`}
          </button>
          <button
            className="cancel-btn"
            onClick={() => navigate('/bridge-club/scan-and-go')}
            disabled={processing}
          >
            Continue Shopping
          </button>
        </div>

        <p className="secure-notice">
          Payments processed securely by Helcim
        </p>
      </div>
    </BridgeClubLayout>
  );
};

export default ScanAndGoCheckout;
